import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatErr, imgUrl } from "@/lib/api";
import { AdminTitle } from "@/pages/admin/AdminLayout";
import { ImageUploader } from "./ImageUploader";
import { PhotoGrid } from "./PhotoGrid";
import { ChaptersField } from "./ChaptersField";

const inputCls = "admin-input";

const Field = ({ f, value, onChange, form }) => {
  const id = `field-${f.key}`;
  if (f.type === "textarea") return <textarea className={`${inputCls} min-h-[100px]`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
  if (f.type === "custom") return f.render(form);
  if (f.type === "chapters") return <ChaptersField value={value} onChange={onChange} cover={form.cover} onCover={(p) => form.__setCover(p)} />;
  if (f.type === "select") return <select className={inputCls} value={f.bool ? String(value) : value} onChange={(e) => onChange(f.bool ? e.target.value === "true" : f.number ? Number(e.target.value) : e.target.value)} data-testid={id}>{f.options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}</select>;
  if (f.type === "switch") return <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={id} /> {f.hint}</label>;
  if (f.type === "date") return <input className={inputCls} type="date" lang="it" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
  if (f.type === "number") return <input className={inputCls} type="number" min="0" step="0.01" placeholder={f.placeholder} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid={id} />;
  if (f.type === "list") return <textarea className={`${inputCls} min-h-[100px]`} placeholder="Una voce per riga" value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} data-testid={id} />;
  if (f.type === "image") return (
    <div className="flex items-center gap-3">
      {value && <img src={imgUrl(value)} alt="" className="w-20 h-20 object-cover" />}
      <ImageUploader multiple={false} label={value ? "Sostituisci" : "Carica immagine"} onUploaded={(u) => onChange(u[0])} testid={`${id}-upload`} />
    </div>
  );
  if (f.type === "images") return (
    <div>
      <ImageUploader onUploaded={(u) => onChange([...(value || []), ...u])} testid={`${id}-upload`} />
      <span className="text-xs text-mute ml-3">{(value || []).length} foto · stella = copertina</span>
      <PhotoGrid photos={value || []} onChange={onChange} cover={form.cover} onCover={(p) => form.__setCover(p)} testid={`${id}-grid`} />
    </div>
  );
  return <input className={inputCls} value={value ?? ""} placeholder={f.placeholder} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
};

const clean = (form, fields) => {
  const out = {};
  fields.forEach((f) => { out[f.key] = f.type === "list" ? (form[f.key] || []).map((s) => s.trim()).filter(Boolean) : form[f.key]; });
  if ("cover" in form && !("cover" in out)) out.cover = form.cover;
  return out;
};

export default function ResourceManager({ title, subtitle, endpoint, fields, defaults, card, toggle, testid, wide }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get(`/admin/${endpoint}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [endpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      const body = clean(form, fields);
      if (form.id) await api.put(`/admin/${endpoint}/${form.id}`, body); else await api.post(`/admin/${endpoint}`, body);
      toast.success("Salvato"); setForm(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (it) => {
    if (!window.confirm("Eliminare definitivamente?")) return;
    await api.delete(`/admin/${endpoint}/${it.id}`); toast.success("Eliminato"); load();
  };
  const flip = async (it) => {
    try { await api.put(`/admin/${endpoint}/${it.id}`, clean({ ...it, [toggle.key]: !it[toggle.key] }, fields)); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  const formWithCover = form && { ...form, __setCover: (p) => setForm({ ...form, cover: p }) };

  return (
    <div data-testid={testid}>
      <AdminTitle title={title} subtitle={subtitle}>
        <button onClick={() => setForm({ ...defaults })} className="btn-ink !py-2.5 !px-5" data-testid={`${testid}-new-btn`}><Plus size={14} /> Aggiungi</button>
      </AdminTitle>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((it, k) => {
          const c = card(it);
          return (
            <div key={it.id} className="admin-card overflow-hidden flex flex-col" data-testid={`${testid}-item-${k}`}>
              {c.image !== undefined && <div className="h-44 bg-cream">{c.image && <img src={imgUrl(c.image)} alt="" className="w-full h-full object-cover" />}</div>}
              <div className="p-5 flex-1">
                <div className="flex justify-between gap-3"><p className="text-base font-semibold leading-tight">{c.title}</p>{c.badge}</div>
                {c.meta && <p className="text-xs text-mute mt-2">{c.meta}</p>}
                {c.body && <p className="text-sm text-stoneish mt-3 line-clamp-3">{c.body}</p>}
              </div>
              <div className="flex border-t border-line text-xs">
                {toggle && (
                  <button onClick={() => flip(it)} className="flex-1 py-3 flex items-center justify-center gap-1.5 hover:bg-cream" data-testid={`${testid}-toggle-${k}`}>
                    {it[toggle.key] ? <><EyeOff size={13} /> {toggle.off}</> : <><Eye size={13} /> {toggle.on}</>}
                  </button>
                )}
                <button onClick={() => setForm({ ...defaults, ...it })} className="flex-1 py-3 flex items-center justify-center gap-1.5 hover:bg-cream border-l border-line" data-testid={`${testid}-edit-${k}`}><Pencil size={13} /> Modifica</button>
                <button onClick={() => remove(it)} className="py-3 px-4 text-terracotta hover:bg-cream border-l border-line" data-testid={`${testid}-delete-${k}`}><Trash2 size={13} /></button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="text-mute text-sm" data-testid={`${testid}-empty`}>Nessun elemento. Clicca "Aggiungi" per iniziare.</p>}
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className={`admin-root ${wide ? "max-w-4xl" : "max-w-xl"} bg-white rounded-md max-h-[90vh] overflow-y-auto`} data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle className="text-xl font-semibold">{form?.id ? "Modifica" : "Nuovo"}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid sm:grid-cols-2 gap-4">
              {fields.filter((f) => !f.show || f.show(form)).map((f) => (
                <label key={f.key} className={`block ${f.half ? "" : "sm:col-span-2"}`}>
                  <span className="text-xs text-mute">{f.label}</span>
                  <div className="mt-1"><Field f={f} form={formWithCover} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} /></div>
                </label>
              ))}
              <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
                <button onClick={() => setForm(null)} className="btn-line !py-2.5 !px-5" data-testid={`${testid}-cancel-btn`}>Annulla</button>
                <button onClick={save} className="btn-ink !py-2.5 !px-5" data-testid={`${testid}-save-btn`}>Salva</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
